import { Node, NodeEditor } from 'rete'

/** Horizontal gutter between layers (columns). */
const H_GAP = 320
/** Vertical gutter between stacked nodes within a column. */
const V_GAP = 60

/** Fallback node footprint when an element isn't mounted to measure. */
const DEFAULT_W = 250
const DEFAULT_H = 80

type Size = { w: number; h: number }

/**
 * Re-arrange the graph into clean left→right layers.
 *
 * Only the nodes in `newIds` are eligible to move — the user's existing nodes
 * stay exactly where they are and act as anchors, so an agent edit tidies the
 * freshly-added components around the existing model rather than reshuffling
 * the whole canvas. When the graph contains *only* new nodes (e.g. the first
 * agent-built model) it falls back to a full layered layout.
 *
 * `canvas` is the editor's canvas DOM element, used to measure real node sizes.
 */
export function autoLayoutNodes(
  editor: NodeEditor,
  canvas: HTMLElement | null,
  newIds: Set<number>,
): void {
  const nodes = editor.nodes
  if (nodes.length === 0) return

  const view = editor.view as unknown as { nodes?: Map<Node, { update?: () => void }> }

  // Only the nodes the agent created this turn are eligible to move; the
  // user's existing nodes stay exactly where they are and act as anchors.
  const newNodes = nodes.filter(n => newIds.has(n.id))
  const oldNodes = nodes.filter(n => !newIds.has(n.id))
  // Nothing freshly added → leave the canvas untouched.
  if (newNodes.length === 0) return

  // Build predecessor sets from the connection graph: target node ←
  // every node that feeds one of its inputs.
  const preds = new Map<number, Set<number>>()
  nodes.forEach(n => preds.set(n.id, new Set<number>()))
  nodes.forEach(n => {
    n.outputs.forEach(output => {
      output.connections.forEach(conn => {
        const target = conn.input?.node
        if (target && preds.has(target.id)) preds.get(target.id)!.add(n.id)
      })
    })
  })

  // Longest-path layering → each node's column = max(pred column) + 1.
  // Iterate to a fixed point; the guard caps work on the (unexpected)
  // event of a cycle so we never spin.
  const depth = new Map<number, number>()
  nodes.forEach(n => depth.set(n.id, 0))
  let changed = true
  let guard = 0
  while (changed && guard++ <= nodes.length + 5) {
    changed = false
    nodes.forEach(n => {
      let d = 0
      preds.get(n.id)!.forEach(p => { d = Math.max(d, (depth.get(p) ?? 0) + 1) })
      if (d !== (depth.get(n.id) ?? 0)) { depth.set(n.id, d); changed = true }
    })
  }

  // Measure each node's real footprint so tall nodes (e.g. a land-cover
  // dataset with 20+ boolean outputs) get the vertical room they need
  // instead of overlapping the node below. offsetWidth/Height are in the
  // same editor-space units as node.position (the area applies zoom on
  // top), and don't depend on position, so measuring before moving is
  // fine. Falls back to a count-based estimate if the element isn't
  // mounted yet.
  const sizeOf = (n: Node): Size => {
    const el = canvas?.querySelector(`[data-node-id="${n.id}"]`) as HTMLElement | null
    if (el && (el.offsetHeight || el.offsetWidth)) {
      return { w: el.offsetWidth || DEFAULT_W, h: el.offsetHeight || DEFAULT_H }
    }
    const rows = Math.max(n.inputs.size, n.outputs.size)
    const controls = n.controls.size
    return { w: DEFAULT_W, h: 60 + rows * 26 + controls * 30 }
  }
  const sizes = new Map<number, Size>()
  nodes.forEach(n => sizes.set(n.id, sizeOf(n)))

  const depthOf = (n: Node) => depth.get(n.id) ?? 0

  // Max node width per column (over all nodes) — used to size the gutters.
  const colWidth = new Map<number, number>()
  nodes.forEach(n => {
    const d = depthOf(n)
    const w = sizes.get(n.id)?.w ?? DEFAULT_W
    colWidth.set(d, Math.max(colWidth.get(d) ?? 0, w))
  })
  const sortedDepths = Array.from(new Set(nodes.map(depthOf))).sort((a, b) => a - b)

  // Fresh graph (no anchors): fall back to a full layered layout so the
  // very first agent-built model still gets tidied end to end.
  if (oldNodes.length === 0) {
    let originX = Infinity
    let originY = Infinity
    nodes.forEach(n => {
      const p = n.position ?? [0, 0]
      originX = Math.min(originX, p[0])
      originY = Math.min(originY, p[1])
    })
    if (!Number.isFinite(originX)) { originX = 0; originY = 0 }

    let colX = originX
    sortedDepths.forEach(d => {
      const colNodes = nodes.filter(n => depthOf(n) === d)
      colNodes.sort((a, b) => (a.position?.[1] ?? 0) - (b.position?.[1] ?? 0))
      let y = originY
      colNodes.forEach(n => {
        const { h } = sizes.get(n.id) ?? { w: DEFAULT_W, h: DEFAULT_H }
        n.position = [colX, y]
        view.nodes?.get(n)?.update?.()
        y += h + V_GAP
      })
      colX += (colWidth.get(d) ?? DEFAULT_W) + H_GAP
    })
  } else {
    // Anchor column X positions to where the user's existing nodes sit,
    // then extrapolate for any columns that contain only new nodes.
    const anchorX = new Map<number, number>()
    oldNodes.forEach(n => {
      const d = depthOf(n)
      const x = n.position?.[0] ?? 0
      anchorX.set(d, Math.min(anchorX.get(d) ?? Infinity, x))
    })
    // Forward fill: a column with no anchor sits one gutter right of the
    // nearest anchored column to its left.
    for (const d of sortedDepths) {
      if (anchorX.has(d)) continue
      const prev = sortedDepths.filter(p => p < d && anchorX.has(p)).pop()
      if (prev !== undefined) {
        anchorX.set(d, anchorX.get(prev)! + (colWidth.get(prev) ?? DEFAULT_W) + H_GAP)
      }
    }
    // Backward fill for any columns to the left of every anchor.
    for (const d of [...sortedDepths].reverse()) {
      if (anchorX.has(d)) continue
      const next = sortedDepths.find(p => p > d && anchorX.has(p))
      if (next !== undefined) {
        anchorX.set(d, anchorX.get(next)! - ((colWidth.get(d) ?? DEFAULT_W) + H_GAP))
      }
    }

    // Vertical baseline per column: drop new nodes just below the lowest
    // existing node in that column, so they never overlap user nodes.
    const colBottom = new Map<number, number>()
    let graphTop = Infinity
    oldNodes.forEach(n => {
      const d = depthOf(n)
      const y = n.position?.[1] ?? 0
      const h = sizes.get(n.id)?.h ?? DEFAULT_H
      colBottom.set(d, Math.max(colBottom.get(d) ?? -Infinity, y + h))
      graphTop = Math.min(graphTop, y)
    })
    if (!Number.isFinite(graphTop)) graphTop = 0

    // Place new nodes column by column, stacking under the baseline.
    const newByDepth = new Map<number, Node[]>()
    newNodes.forEach(n => {
      const d = depthOf(n)
      if (!newByDepth.has(d)) newByDepth.set(d, [])
      newByDepth.get(d)!.push(n)
    })
    Array.from(newByDepth.keys()).sort((a, b) => a - b).forEach(d => {
      const colNodes = newByDepth.get(d)!
      colNodes.sort((a, b) => (a.position?.[1] ?? 0) - (b.position?.[1] ?? 0))
      const x = anchorX.get(d) ?? 0
      // Start below existing nodes in this column, or at the graph top for
      // a brand-new column off to the side.
      let y = colBottom.has(d) ? colBottom.get(d)! + V_GAP : graphTop
      colNodes.forEach(n => {
        const { h } = sizes.get(n.id) ?? { w: DEFAULT_W, h: DEFAULT_H }
        n.position = [x, y]
        view.nodes?.get(n)?.update?.()
        y += h + V_GAP
      })
    })
  }

  // Re-draw the wires at the new positions, then persist (also creates a
  // single undo entry so the rearrange can be reverted).
  editor.nodes.forEach(n => editor.view?.updateConnections({ node: n }))
  editor.trigger("process")
}
